import React, { ReactElement, ReactNode, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import cn from 'clsx';
import { Options } from 'highcharts';
import { HighchartsReact } from 'highcharts-react-official';
import { HighchartsTooltipElementProps } from '../charts/combo-column-line-chart';
import PanelErrorBoundary from '../panel-error-boundary/panel-error-boundary';
import styles from './style.module.scss';
import { GraphType } from '@components-clean/central-monitoring/panels/panel-constants';
import PanelDisplay from '@components-clean/central-monitoring/panels/panel-display';
import Card from '@components-clean/core/card';
import { IPopoutInfo } from '@components-clean/core/charts/sankey';
import GridMenu from '@components-clean/core/grid/grid-menu';
import GridViewMenu, { GridViewMenuItem } from '@components-clean/core/grid/grid-menu/grid-view-menu';
import { Icon } from '@components-clean/core/icons/icon';
import { SimplifiedPanelIcon } from '@components-clean/core/icons/simplified-panel-icon';
import { UnfilterableIcon } from '@components-clean/core/icons/unfilterable-icon/unfilterable-icon';
import Information from '@components-clean/core/information';
import Loading from '@components-clean/core/loading/loading';
import { PopOutPage } from '@components-clean/core/page-toolbar/page-toolbar';
import { useCountrySelectorContext } from '@contexts-clean/country-selector-context';
import { usePageToolbar } from '@contexts-clean/page-toolbar-context';
import { DataSource, ModelConfig, PanelData, SeriesConfig } from '@interfaces-clean/panels.interface';
import { PanelAverageLineThresholdType, PanelDisplayUnits, PanelTheme, PanelType } from 'types/graphql-global-types';
import { useAuthorization, useTranslation } from '@hooks/index';

import 'highcharts/esm/modules/exporting.js';
import 'highcharts/esm/modules/offline-exporting.js';
import { AllPaths } from '@utils/router/all-paths';

interface PanelFrameProps {
    title?: string | null;
    'data-testid': string;
    infoText?: JSX.Element;
    panelData: PanelData;
    panelType: PanelType;
    graphType?: GraphType;
    modelConfig?: ModelConfig;
    showLegend?: boolean;
    disablePanelSetup?: boolean;
    hidePanelSetup?: boolean;
    TooltipElement?: (props: HighchartsTooltipElementProps) => ReactElement;
    theme?: PanelTheme;
    siteId?: number;
    xAxisTitle?: string;
    yAxisTitle?: string;
    thresholdType?: PanelAverageLineThresholdType | null;
    seriesConfig?: SeriesConfig;
    className?: string;
    dataSource?: DataSource;
    displayUnits?: PanelDisplayUnits | null;
    showxAxisLabels?: boolean;
    customOptions?: Options;
    showWaterMark?: boolean;
    showScreenFilterableWarning?: boolean;
    simplifiedResult?: string;
    nonFilterable?: boolean;
    dataviewRef?: string | null;
    popoutInfo?: IPopoutInfo;
    exportFilename?: string;
    isLoading?: boolean;
    version: number;
    isPreview?: boolean;
    customEmptyPanelMessage?: string;
    preChartContent?: ReactNode;
    customHeight?: number;
}

const PanelFrame = ({
    title,
    'data-testid': testId,
    infoText,
    panelData,
    showLegend,
    disablePanelSetup = false,
    hidePanelSetup = false,
    TooltipElement,
    panelType,
    graphType,
    theme,
    siteId,
    xAxisTitle,
    yAxisTitle,
    thresholdType,
    seriesConfig,
    className,
    modelConfig,
    dataSource,
    displayUnits,
    showxAxisLabels,
    customOptions,
    showWaterMark,
    showScreenFilterableWarning,
    simplifiedResult,
    nonFilterable,
    dataviewRef,
    popoutInfo,
    exportFilename,
    isLoading,
    version,
    isPreview = false,
    customEmptyPanelMessage,
    preChartContent,
    customHeight,
}: PanelFrameProps) => {
    const { t } = useTranslation();
    const chartRef = useRef<HighchartsReact.RefObject | null>(null);
    const { legendToggle$ } = usePageToolbar();
    const user = useAuthorization();
    const { country } = useCountrySelectorContext();

    const createPopoutDataViewRoute = () => {
        const basePath = AllPaths.path('cm2-data-views', { dataView: popoutInfo?.dataviewRef });

        const queryParams = new URLSearchParams(location.search);

        if (country?.value) {
            queryParams.set('country', country.value);
        } else {
            queryParams.delete('country');
        }

        // ensures an empty queryParams object doesn't result in a '?' being added to the URL'
        const queryParamsString = queryParams.toString();

        return queryParamsString ? `${basePath}?${queryParamsString}` : basePath;
    };

    useEffect(() => {
        const subscription = legendToggle$.subscribe((value) => {
            if (!chartRef?.current?.chart) return;

            // First update re-draws the chart, turning off animation
            chartRef.current.chart.update(
                {
                    legend: {
                        enabled: value,
                    },
                    chart: { animation: false },
                },
                true,
            );
            // Second update turns animations back on but does not trigger a redraw
            chartRef.current.chart.update(
                {
                    chart: { animation: true },
                },
                false,
            );
        });

        return () => subscription.unsubscribe();
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, []);

    const gridViewOptions: GridViewMenuItem[] = [
        {
            label: t('cm:Fullscreen'),
            onClick: () => {
                if (chartRef?.current?.chart) chartRef.current.chart.fullscreen.open();
            },
            icon: (
                <FontAwesomeIcon
                    icon={['far', 'up-right-and-down-left-from-center']}
                    fontSize={16}
                    className="k-mt--1px"
                />
            ),
            isActive: false,
        },
        {
            label: t('cm:panelSetup'),
            icon: <FontAwesomeIcon icon={['far', 'pen-to-square']} fontSize={16} className="k-mt--1px" />,
            onClick: () => {},
            isActive: false,
            isDisabled: disablePanelSetup,
            isHidden: hidePanelSetup,
        },
        {
            label: t('cm:PrintChart'),
            icon: <FontAwesomeIcon icon={['far', 'print']} fontSize={16} className="k-mt--1px" />,
            onClick: () => {
                // The timeout gives the menu enough time to close, otherwise coming back from the print screen
                // results in a partially collapsed menu being left behind. Bug #53409
                if (chartRef?.current?.chart) setTimeout(() => chartRef.current?.chart.print(), 350);
            },
            isActive: false,
        },
        {
            label: t('cm:DownloadPNG'),
            icon: <FontAwesomeIcon icon={['fal', 'file-image']} fontSize={16} className="k-mt--1px" />,
            onClick: () => {
                if (chartRef?.current?.chart) chartRef.current.chart.exportChartLocal(undefined);
            },
            isActive: false,
        },
        {
            label: t('cm:DownloadJPEG'),
            icon: <FontAwesomeIcon icon={['far', 'file-image']} fontSize={16} className="k-mt--1px" />,
            onClick: () => {
                if (chartRef?.current?.chart) chartRef.current.chart.exportChartLocal({ type: 'image/jpeg' });
            },
            isActive: false,
        },
        {
            label: t('cm:DownloadSVG'),
            icon: <FontAwesomeIcon icon={['far', 'file-code']} fontSize={16} className="k-mt--1px" />,
            onClick: () => {
                if (chartRef?.current?.chart) chartRef.current?.chart.exportChartLocal({ type: 'image/svg+xml' });
            },
            isActive: false,
        },
    ];

    return (
        <>
            {' '}
            {isPreview &&
                (isLoading ? (
                    <Loading />
                ) : (
                    <PanelErrorBoundary>
                        <PanelDisplay
                            type={panelType}
                            chartConfig={{
                                type: graphType,
                                xAxisTitle,
                                yAxisTitle,
                                plotTitle: title ?? undefined,
                                thresholdType,
                                seriesConfig,
                                displayUnits,
                                showxAxisLabels,
                                customOptions,
                            }}
                            modelConfig={modelConfig}
                            chartRef={chartRef}
                            panelData={panelData}
                            showLegend={showLegend}
                            TooltipElement={TooltipElement}
                            theme={theme}
                            siteId={siteId}
                            dataSource={dataSource}
                            showSubTitle={false}
                            showWaterMark={showWaterMark}
                            dataviewRef={dataviewRef}
                            popoutInfo={popoutInfo}
                            exportFilename={exportFilename}
                            version={version}
                            isPreview={isPreview}
                            customEmptyPanelMessage={customEmptyPanelMessage}
                        />
                    </PanelErrorBoundary>
                ))}
            {!isPreview && (
                <Card
                    data-testid={testId}
                    title={<div>{title}</div>}
                    className={className}
                    headerActions={
                        <div className={cn('k-display-flex k-align-items-center k-gap-2', styles.iconsColor)}>
                            {popoutInfo?.showPopoutIcon && (
                                <button
                                    type="button"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        const route = createPopoutDataViewRoute();
                                        PopOutPage(user?.organisationKey as string, route);
                                    }}
                                    className={cn('k-bg-transparent k-border-0 k-p-0 k-cursor-pointer', styles.button)}
                                >
                                    <Icon
                                        dataTestId="pop-out-button"
                                        icon={['far', 'arrow-up-right-from-square']}
                                        aria-label="Pop Out Page"
                                        data-test-id="pop-out-button"
                                        className={styles.iconPrimary}
                                        title={t('cm:controlsHub.panel.dataviewPopOutTitle')}
                                        tooltipPosition="top"
                                        minWidth={170}
                                    />
                                </button>
                            )}
                            {simplifiedResult && (
                                <SimplifiedPanelIcon dataTestId={`${testId}_simplified`} tooltip={simplifiedResult} />
                            )}
                            {(showScreenFilterableWarning || nonFilterable) && (
                                <UnfilterableIcon
                                    dataTestId={`${testId}_unfilterable`}
                                    tooltip={t('cm:componentsClean.panels.screenFilterableWarning')}
                                />
                            )}
                            {infoText && <Information title={infoText as string} />}
                            <div className="k-ml--2.5">
                                <GridMenu
                                    items={[
                                        {
                                            title: '',
                                            icon: ['far', 'bars'],
                                            iconFontSize: '16px',
                                            'data-testid': 'panel-action-menu',
                                            children: <GridViewMenu viewOptions={gridViewOptions} />,
                                        },
                                    ]}
                                />
                            </div>
                        </div>
                    }
                >
                    {isLoading ? (
                        <Loading />
                    ) : (
                        <PanelErrorBoundary>
                            {preChartContent && <div>{preChartContent}</div>}
                            <PanelDisplay
                                type={panelType}
                                chartConfig={{
                                    type: graphType,
                                    xAxisTitle,
                                    yAxisTitle,
                                    plotTitle: title ?? undefined,
                                    thresholdType,
                                    seriesConfig,
                                    displayUnits,
                                    showxAxisLabels,
                                    customOptions,
                                }}
                                modelConfig={modelConfig}
                                chartRef={chartRef}
                                panelData={panelData}
                                showLegend={showLegend}
                                TooltipElement={TooltipElement}
                                theme={theme}
                                siteId={siteId}
                                dataSource={dataSource}
                                showSubTitle={false}
                                showWaterMark={showWaterMark}
                                dataviewRef={dataviewRef}
                                popoutInfo={popoutInfo}
                                exportFilename={exportFilename}
                                version={version}
                                isPreview={isPreview}
                                customEmptyPanelMessage={customEmptyPanelMessage}
                                customHeight={customHeight}
                            />
                        </PanelErrorBoundary>
                    )}
                </Card>
            )}
        </>
    );
};

export default PanelFrame;
